/**
 * auth.ts — sign-in pill + OTP modal, shared across landing/app/account.
 *
 * UX rules:
 *   - Guests are first-class. We NEVER block the map or the wall behind
 *     auth. The default visit is anonymous.
 *   - The sign-in pill sits in the top-right, subtle. It says either
 *     "Sign in" (guest) or "Account" (signed in). Tap → either opens the
 *     OTP modal or navigates to /account.
 *   - The OTP modal is two screens:
 *       1) Phone (with country-code chooser, default +61 / +91 by locale)
 *       2) Six-digit code entry, with auto-advance + paste-friendly input
 *   - The OTP message itself is clean and trustworthy — see
 *     functions/_lib/sms.js::composeOtpMessage.
 *
 * Persistence:
 *   - Server-issued HttpOnly session cookie. We can't read it from JS, so
 *     we cache the user's display name in localStorage purely for instant
 *     UI hydration ("Welcome back, Kavi" before the network round-trip).
 */

// Token constants interpolated (not var()): this sheet also mounts on
// /account/, whose inline :root is a PARTIAL token set (no --surface /
// --hairline / gold-alpha ramp) — var() would silently fail there. The
// TS projection works on every page.
import { BP_MOBILE, COLOR } from "../ui/tokens";

const NAME_CACHE_KEY = "hyperlocal.auth.name";

interface MeUser {
  uid: string;
  displayName: string;
  createdAt: number;
}

let CURRENT_USER: MeUser | null = null;

/**
 * Wrapped fetch with a hard timeout. Without this, a hung Pages Function
 * leaves the user staring at a "Sending..." button forever — the issue
 * the user reported as "Sign in is not working" was at minimum
 * exacerbated by this. 12s is generous enough for cold-start latency
 * but short enough that the user gets a clear error if something is off.
 */
async function authFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const ctrl = new AbortController();
  const t = window.setTimeout(() => ctrl.abort(), 12_000);
  try {
    return await fetch(input, { ...init, signal: ctrl.signal });
  } finally {
    window.clearTimeout(t);
  }
}

/**
 * Best-effort error-reason extraction. Treats any non-2xx as a failure
 * with a message the user can act on. The dev-mock returns a structured
 * 404 with a `message` field when the API isn't wired — surface that
 * verbatim so the developer (the user is testing in dev) sees WHY auth
 * isn't responding instead of a generic "Network error".
 */
async function reasonFromResponse(res: Response, fallback: string): Promise<string> {
  try {
    const data = await res.json();
    if (data && data.message) return String(data.message);
    if (data && data.error) return String(data.error);
  } catch { /* ignore */ }
  // 404 here means the request reached the static Vite/Pages server but
  // the API mock or real Function wasn't there. Most common causes:
  //   - dev server was started before the dev plugin was last edited
  //     (Vite plugins don't hot-reload; restart `npm run dev`),
  //   - someone is on `npm run preview` with an OLD plugin that doesn't
  //     register configurePreviewServer (also fixed by restart),
  //   - production deploy is missing functions/api/auth/request-otp.js.
  if (res.status === 404) return "Sign-in API isn't responding (404). Restart `npm run dev` (or `npm run preview`) so the API mock loads.";
  if (res.status === 503) return "Sign-in is offline right now. Try again in a moment.";
  return fallback;
}

/* -------------------------------------------------------------------------- */
/*  Styles (one-shot)                                                         */
/* -------------------------------------------------------------------------- */

let AUTH_STYLES_INJECTED = false;
function ensureStyles() {
  if (AUTH_STYLES_INJECTED) return;
  AUTH_STYLES_INJECTED = true;
  const s = document.createElement("style");
  s.id = "hyperlocal-auth-styles";
  s.textContent = `
  /* ----- Top pill -----
     Default position: top-right (used on /landing and /account where the
     right side is empty). On /app the right side is crowded — city toggle
     at top:16px and Suburb-filter pill at top:58px both anchor right:16px.
     Stacking the auth pill there caused it to butt against the Suburb
     filter (https://… screenshot Apr 2026). On /app we move it to the
     top-LEFT instead so it has its own corner, never collides with map
     chrome, and stays one tap from the user's thumb. */
  #hyperlocal-auth-pill {
    position: fixed;
    top: 14px;
    right: 14px;
    z-index: 1320; /* above filter FAB (1250) and city toggle (1200) */
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 13px;
    border-radius: 999px;
    background: rgba(22,22,22,0.85);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border: 1px solid rgba(255,214,128,0.25);
    color: ${COLOR.gold2};
    font: 600 12px/1 system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
    letter-spacing: 0.03em;
    text-decoration: none;
    cursor: pointer;
    transition: background 140ms ease, border-color 140ms ease;
    max-width: 38vw;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  #hyperlocal-auth-pill:hover {
    background: ${COLOR.goldA16};
    border-color: rgba(255,214,128,0.45);
  }
  #hyperlocal-auth-pill:active { transform: translateY(1px); }
  #hyperlocal-auth-pill[data-state="user"] {
    background: rgba(58,46,28,0.92);
    color: ${COLOR.gold2};
  }
  /* On /app the right corner is occupied by city-toggle + Suburb filter,
     so we park the auth pill on the LEFT. The home-chip ("← HYPERLOCAL")
     also lives top-left at z-index 2500 — the pill must clear it or the
     chip will draw over our "Sign in" label.
       Desktop home-chip: top:14 left:14, measured width 108px (uppercase
                          letter-spaced 11px text) → right edge at 122,
                          pill starts at left:140 with an 18px breathing gap.
       Mobile  home-chip: top:12 left:12, 32×32 circle → pill starts at left:56.
     The Compare pill sits at top:50 left:16 (desktop only) so the auth
     pill rides on a clear row at top:14 — they don't stack. */
  body[data-hyperlocal-page="app"] #hyperlocal-auth-pill {
    top: 14px;
    left: 140px;
    right: auto;
  }
  @media (min-width: 721px) {
    /* Desktop /app: subtle shadow so the pill reads as a separate chit
       from the home-chip beside it, without an aggressive border. */
    body[data-hyperlocal-page="app"] #hyperlocal-auth-pill {
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    }
  }
  @media ${BP_MOBILE} {
    #hyperlocal-auth-pill { padding: 6px 11px; font-size: 11.5px; max-width: 42vw; }
    /* On phones the home-chip collapses to a 32px circle at left:12, so
       we only need 56px of clearance for the pill to start. */
    body[data-hyperlocal-page="app"] #hyperlocal-auth-pill {
      top: 12px;
      left: 56px;
    }
  }

  /* ----- Modal ----- */
  #hyperlocal-auth-modal {
    position: fixed;
    inset: 0;
    z-index: 3200;
    display: none;
    align-items: center;
    justify-content: center;
    background: rgba(0,0,0,0.6);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    padding: 16px;
    padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  }
  #hyperlocal-auth-modal[data-open="1"] { display: flex; }
  #hyperlocal-auth-modal .am-card {
    width: min(440px, 100%);
    background: ${COLOR.surface};
    border: 1px solid ${COLOR.goldA22};
    border-radius: 18px;
    box-shadow: 0 16px 40px rgba(0,0,0,0.6);
    padding: 22px 22px 18px;
    color: ${COLOR.ink};
    animation: am-pop 200ms ease-out;
    position: relative;
  }
  @keyframes am-pop {
    from { transform: translateY(8px) scale(0.98); opacity: 0; }
    to   { transform: translateY(0) scale(1); opacity: 1; }
  }
  #hyperlocal-auth-modal .am-close {
    position: absolute; top: 10px; right: 12px;
    background: transparent; border: 0; color: #8a8d93;
    font-size: 22px; line-height: 1; cursor: pointer;
  }
  #hyperlocal-auth-modal .am-h {
    font: 800 19px/1.2 system-ui, sans-serif;
    color: ${COLOR.gold2}; margin-bottom: 4px;
  }
  #hyperlocal-auth-modal .am-sub {
    font: 13.5px/1.5 system-ui, sans-serif;
    color: #b8bbc1; margin-bottom: 16px;
  }
  #hyperlocal-auth-modal .am-row {
    display: flex; gap: 8px; align-items: stretch;
  }
  #hyperlocal-auth-modal select.am-cc, #hyperlocal-auth-modal input.am-phone {
    background: #25272b;
    border: 1px solid #3a3c41;
    color: ${COLOR.ink};
    border-radius: 12px;
    padding: 11px 12px;
    font: 15px/1.2 system-ui, sans-serif;
    outline: none;
  }
  #hyperlocal-auth-modal select.am-cc { min-width: 92px; padding-right: 8px; }
  #hyperlocal-auth-modal input.am-phone { flex: 1; font-variant-numeric: tabular-nums; }
  #hyperlocal-auth-modal input.am-phone:focus,
  #hyperlocal-auth-modal select.am-cc:focus { border-color: ${COLOR.gold}; box-shadow: 0 0 0 3px ${COLOR.goldA12}; }
  #hyperlocal-auth-modal .am-cta {
    margin-top: 16px;
    display: block;
    width: 100%;
    background: linear-gradient(180deg, ${COLOR.gold}, #f4b254);
    color: #1a1a1a;
    border: 0;
    padding: 12px;
    border-radius: 12px;
    font: 700 14px/1 system-ui, sans-serif;
    cursor: pointer;
    transition: filter 140ms ease;
  }
  #hyperlocal-auth-modal .am-cta:hover { filter: brightness(1.05); }
  #hyperlocal-auth-modal .am-cta[disabled] { background: #5a4f37; color: #2a261f; cursor: not-allowed; }
  #hyperlocal-auth-modal .am-err {
    margin-top: 10px;
    color: #ff8e8e;
    font: 12.5px/1.45 system-ui, sans-serif;
    display: none;
  }
  #hyperlocal-auth-modal .am-err[data-show="1"] { display: block; }

  /* OTP digit row */
  #hyperlocal-auth-modal .am-otp-row {
    display: flex;
    gap: 8px;
    justify-content: center;
    margin: 8px 0 4px;
  }
  #hyperlocal-auth-modal .am-otp-row input {
    width: 44px; height: 54px;
    text-align: center;
    background: #25272b;
    border: 1px solid #3a3c41;
    color: ${COLOR.gold2};
    border-radius: 10px;
    font: 700 22px/1 ui-monospace, "SF Mono", Menlo, monospace;
    outline: none;
    -moz-appearance: textfield;
  }
  #hyperlocal-auth-modal .am-otp-row input::-webkit-outer-spin-button,
  #hyperlocal-auth-modal .am-otp-row input::-webkit-inner-spin-button {
    -webkit-appearance: none; margin: 0;
  }
  #hyperlocal-auth-modal .am-otp-row input:focus { border-color: ${COLOR.gold}; box-shadow: 0 0 0 3px ${COLOR.goldA12}; }
  #hyperlocal-auth-modal .am-resend {
    text-align: center;
    margin-top: 10px;
    font: 12.5px/1.4 system-ui, sans-serif;
    color: #8a8d93;
  }
  #hyperlocal-auth-modal .am-resend button {
    background: transparent; border: 0;
    color: ${COLOR.gold}; font: 600 12.5px/1 system-ui, sans-serif;
    text-decoration: underline; cursor: pointer; padding: 0;
  }
  #hyperlocal-auth-modal .am-resend button[disabled] { color: #6c6f75; cursor: not-allowed; text-decoration: none; }
  #hyperlocal-auth-modal .am-trust {
    margin-top: 14px;
    padding-top: 12px;
    border-top: 1px dashed rgba(255,214,128,0.2);
    color: #8a8d93;
    font: 11.5px/1.45 system-ui, sans-serif;
  }
  #hyperlocal-auth-modal .am-trust strong { color: #c2c5cb; font-weight: 700; }

  /* Dev-mode hint when SMS_PROVIDER=console — shows the OTP code right in the
     modal so devs can finish the flow without configuring SMS. Only painted
     when the API returns a devCode field. */
  #hyperlocal-auth-modal .am-devcode {
    margin-top: 10px; padding: 8px 10px; border-radius: 8px;
    background: #2a2210; color: ${COLOR.gold2}; border: 1px dashed #6f5a2a;
    font: 12.5px/1.4 ui-monospace, monospace;
    display: none;
  }
  #hyperlocal-auth-modal .am-devcode[data-show="1"] { display: block; }

  /* Phone | Email channel toggle — segmented control at the top of the
     phone screen. Compact, accessible, and clearly indicates which channel
     the OTP will be delivered to. Mirrors iOS / Apple Pay segmented styling. */
  #hyperlocal-auth-modal .am-tabs {
    display: inline-flex;
    gap: 0;
    margin-bottom: 14px;
    background: ${COLOR.surface};
    border: 1px solid #2a2c31;
    border-radius: 999px;
    padding: 3px;
  }
  #hyperlocal-auth-modal .am-tab {
    background: transparent;
    border: 0;
    color: #b8bbc1;
    padding: 7px 16px;
    border-radius: 999px;
    font: 600 12.5px/1 system-ui, sans-serif;
    letter-spacing: 0.02em;
    cursor: pointer;
    transition: background 140ms ease, color 140ms ease;
  }
  #hyperlocal-auth-modal .am-tab:hover { color: ${COLOR.ink}; }
  #hyperlocal-auth-modal .am-tab[aria-selected="true"] {
    background: linear-gradient(180deg, ${COLOR.gold}, #f4b254);
    color: #1a1a1a;
  }
  /* Email-channel input — full-width, no country code box. */
  #hyperlocal-auth-modal input.am-email {
    background: #25272b;
    border: 1px solid #3a3c41;
    color: ${COLOR.ink};
    border-radius: 12px;
    padding: 11px 12px;
    font: 15px/1.2 system-ui, sans-serif;
    outline: none;
    width: 100%;
    box-sizing: border-box;
  }
  #hyperlocal-auth-modal input.am-email:focus {
    border-color: ${COLOR.gold};
    box-shadow: 0 0 0 3px ${COLOR.goldA12};
  }
  /* Channel-aware row visibility. */
  #hyperlocal-auth-modal [data-channel-row="phone"][data-active="0"],
  #hyperlocal-auth-modal [data-channel-row="email"][data-active="0"] { display: none; }
  `;
  document.head.appendChild(s);
}

/* -------------------------------------------------------------------------- */
/*  Pill                                                                      */
/* -------------------------------------------------------------------------- */

let pill: HTMLAnchorElement | null = null;

export function mountAuthPill(): void {
  ensureStyles();
  if (pill) return;
  pill = document.createElement("a");
  pill.id = "hyperlocal-auth-pill";
  pill.setAttribute("role", "button");
  pill.href = "#";
  pill.textContent = "Sign in";
  pill.addEventListener("click", (e) => {
    e.preventDefault();
    if (CURRENT_USER) {
      window.location.href = "/account/";
    } else {
      openAuthModal();
    }
  });
  document.body.appendChild(pill);
  hydrateUserFromCache();
  refreshFromServer();
}

function setPillState(user: MeUser | null) {
  if (!pill) return;
  if (user) {
    pill.setAttribute("data-state", "user");
    pill.textContent = user.displayName ? user.displayName : "Account";
    pill.title = "Open your account";
  } else {
    pill.removeAttribute("data-state");
    pill.textContent = "Sign in";
    pill.title = "Sign in or create an account";
  }
}

function hydrateUserFromCache() {
  try {
    const raw = localStorage.getItem(NAME_CACHE_KEY);
    if (raw) {
      const cached = JSON.parse(raw) as MeUser;
      CURRENT_USER = cached;
      setPillState(cached);
    }
  } catch { /* ignore */ }
}

async function refreshFromServer() {
  try {
    const res = await authFetch("/api/auth/me", { cache: "no-store" });
    if (!res.ok) return;
    const data = await res.json();
    if (data && data.ok && data.user) {
      CURRENT_USER = data.user;
      setPillState(data.user);
      try { localStorage.setItem(NAME_CACHE_KEY, JSON.stringify(data.user)); } catch { /* ignore */ }
    } else {
      CURRENT_USER = null;
      setPillState(null);
      try { localStorage.removeItem(NAME_CACHE_KEY); } catch { /* ignore */ }
    }
  } catch {
    /* offline / aborted — keep cached state */
  }
}

/* -------------------------------------------------------------------------- */
/*  Modal                                                                     */
/* -------------------------------------------------------------------------- */

let modal: HTMLDivElement | null = null;
/**
 * Identity-being-verified state. `channel` selects whether the OTP went to a
 * phone or an email address, and the corresponding field carries the actual
 * identifier we'll send to verify-otp. Tracking BOTH (instead of one
 * polymorphic field) lets us keep the form values intact when the user
 * toggles between channels — no jarring "you typed a number, now it's gone"
 * if they tap Email by mistake.
 */
const phoneState: { dial: string; phone: string; email: string; channel: "phone" | "email" } = {
  dial: "+61",
  phone: "",
  email: "",
  channel: "phone",
};

const COUNTRIES: Array<{ code: string; dial: string; label: string }> = [
  { code: "AU", dial: "+61", label: "🇦🇺 +61" },
  { code: "IN", dial: "+91", label: "🇮🇳 +91" },
  { code: "US", dial: "+1",  label: "🇺🇸 +1"  },
  { code: "GB", dial: "+44", label: "🇬🇧 +44" },
  { code: "AE", dial: "+971", label: "🇦🇪 +971" },
  { code: "SG", dial: "+65", label: "🇸🇬 +65" },
  { code: "NZ", dial: "+64", label: "🇳🇿 +64" },
];

function ensureModal() {
  if (modal) return modal;
  ensureStyles();
  const m = document.createElement("div");
  m.id = "hyperlocal-auth-modal";
  m.setAttribute("role", "dialog");
  m.setAttribute("aria-modal", "true");
  m.setAttribute("aria-label", "Sign in to Hyperlocal");
  m.innerHTML = `
    <div class="am-card">
      <button type="button" class="am-close" aria-label="Close">×</button>
      <div data-screen="phone">
        <div class="am-h">Sign in to save your bubbles</div>
        <p class="am-sub">Get a 6-digit code by SMS or email. No passwords, no spam — promise.</p>
        <div class="am-tabs" role="tablist" aria-label="Choose how to receive the code">
          <button type="button" class="am-tab" role="tab" aria-selected="true" data-channel="phone">Phone</button>
          <button type="button" class="am-tab" role="tab" aria-selected="false" data-channel="email">Email</button>
        </div>
        <div class="am-row" data-channel-row="phone" data-active="1">
          <select class="am-cc" aria-label="Country code">
            ${COUNTRIES.map((c) => `<option value="${c.dial}">${c.label}</option>`).join("")}
          </select>
          <input class="am-phone" type="tel" inputmode="numeric" autocomplete="tel-national" placeholder="400 000 000" maxlength="14"/>
        </div>
        <div class="am-row" data-channel-row="email" data-active="0">
          <input class="am-email" type="email" inputmode="email" autocomplete="email" placeholder="you@example.com" maxlength="120"/>
        </div>
        <button type="button" class="am-cta" disabled>Send code</button>
        <div class="am-err" role="alert"></div>
        <div class="am-trust">
          <strong>What we send:</strong> a one-time 6-digit code, valid for 10 minutes. We never share it. We never call you. You can keep using Hyperlocal as a guest — sign in only saves your map pins so you can revisit them later.
        </div>
      </div>
      <div data-screen="otp" hidden>
        <div class="am-h">Enter your code</div>
        <p class="am-sub">We sent it to <strong class="am-phonemask"></strong>. Auto-fills if your device offers it.</p>
        <div class="am-otp-row">
          ${[0,1,2,3,4,5].map(() => '<input type="tel" inputmode="numeric" pattern="\\d*" maxlength="1" autocomplete="one-time-code"/>').join("")}
        </div>
        <button type="button" class="am-cta" disabled>Verify &amp; sign in</button>
        <div class="am-err" role="alert"></div>
        <div class="am-devcode" aria-live="polite"></div>
        <div class="am-resend">
          Didn't get it? <button type="button" class="am-resend-btn">Resend code</button>
          <span class="am-resend-cd"></span>
        </div>
      </div>
    </div>
  `;
  document.body.appendChild(m);
  modal = m;
  wireModal(m);
  return m;
}

function wireModal(m: HTMLDivElement) {
  const close = () => closeAuthModal();
  m.querySelector(".am-close")?.addEventListener("click", close);
  m.addEventListener("click", (e) => {
    if (e.target === m) close();
  });

  // ---- Phone screen
  const cc = m.querySelector("select.am-cc") as HTMLSelectElement;
  const ph = m.querySelector("input.am-phone") as HTMLInputElement;
  const em = m.querySelector("input.am-email") as HTMLInputElement;
  const phoneCta = m.querySelector('[data-screen="phone"] .am-cta') as HTMLButtonElement;
  const phoneErr = m.querySelector('[data-screen="phone"] .am-err') as HTMLElement;
  const phoneRow = m.querySelector('[data-channel-row="phone"]') as HTMLElement;
  const emailRow = m.querySelector('[data-channel-row="email"]') as HTMLElement;
  const tabs = Array.from(m.querySelectorAll<HTMLButtonElement>('.am-tab'));
  // Default country guess — Australia (testing target). India is fast second.
  cc.value = "+61";
  phoneState.dial = "+61";

  // Quick "is this a plausible email?" — same shape as functions/_lib/email.js
  // ::normalizeEmail(). We don't trust the client validation for security
  // (the server re-validates) but it gates the CTA so the user gets instant
  // feedback instead of a server round-trip telling them to fix it.
  const looksLikeEmail = (s: string): boolean => {
    const t = (s || "").trim().toLowerCase();
    if (t.length === 0 || t.length > 254) return false;
    if (/\s/.test(t)) return false;
    const at = t.indexOf("@");
    if (at <= 0 || at !== t.lastIndexOf("@") || at === t.length - 1) return false;
    const domain = t.slice(at + 1);
    if (!domain.includes(".") || domain.startsWith(".") || domain.endsWith(".")) return false;
    return true;
  };

  const updatePhoneCta = () => {
    if (phoneState.channel === "email") {
      phoneCta.disabled = !looksLikeEmail(em.value);
    } else {
      const digits = ph.value.replace(/\D/g, "");
      phoneCta.disabled = digits.length < 6;
    }
  };
  ph.addEventListener("input", () => {
    ph.value = ph.value.replace(/[^\d\s]/g, "");
    updatePhoneCta();
  });
  em.addEventListener("input", () => {
    // Email validation is gentle — just enable Send when it parses. The
    // server re-validates and returns a clear error if the address is junk.
    updatePhoneCta();
  });
  cc.addEventListener("change", () => {
    phoneState.dial = cc.value;
  });

  // ---- Channel toggle (Phone | Email) -------------------------------------
  // Tab clicks swap which row is visible AND which input is the source of
  // truth for the CTA. We keep the existing values in both rows so a
  // user toggling channels by mistake doesn't lose what they typed.
  const setChannel = (next: "phone" | "email") => {
    phoneState.channel = next;
    for (const t of tabs) t.setAttribute("aria-selected", t.dataset.channel === next ? "true" : "false");
    phoneRow.setAttribute("data-active", next === "phone" ? "1" : "0");
    emailRow.setAttribute("data-active", next === "email" ? "1" : "0");
    // Clear any previous error so a stale "wrong code" from the other channel
    // doesn't confuse the user.
    phoneErr.removeAttribute("data-show");
    phoneErr.textContent = "";
    updatePhoneCta();
    // Auto-focus the active input — small ergonomic touch, matches Apple Pay
    // / iOS form-segment behavior.
    setTimeout(() => {
      if (next === "email") em.focus();
      else ph.focus();
    }, 60);
  };
  for (const t of tabs) {
    t.addEventListener("click", () => setChannel((t.dataset.channel as "phone" | "email") || "phone"));
  }

  phoneCta.addEventListener("click", async () => {
    phoneErr.removeAttribute("data-show");
    const channel = phoneState.channel;
    let body: { phone?: string; email?: string };
    let identityForOtpScreen: string;
    if (channel === "email") {
      const email = em.value.trim().toLowerCase();
      if (!looksLikeEmail(email)) {
        phoneErr.textContent = "That doesn't look like a valid email.";
        phoneErr.setAttribute("data-show", "1");
        return;
      }
      phoneState.email = email;
      identityForOtpScreen = email;
      body = { email };
    } else {
      const digits = ph.value.replace(/\D/g, "");
      const phone = `${cc.value}${digits}`;
      phoneState.phone = phone;
      identityForOtpScreen = phone;
      body = { phone };
    }
    phoneCta.disabled = true;
    phoneCta.textContent = "Sending…";
    try {
      const res = await authFetch("/api/auth/request-otp", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        phoneErr.textContent = await reasonFromResponse(res, "Couldn't send the code. Try again.");
        phoneErr.setAttribute("data-show", "1");
        phoneCta.disabled = false;
        phoneCta.textContent = "Send code";
        return;
      }
      const data = await res.json().catch(() => ({} as any));
      if (!data || !data.ok) {
        phoneErr.textContent = data?.message || "Couldn't send the code. Try again.";
        phoneErr.setAttribute("data-show", "1");
        phoneCta.disabled = false;
        phoneCta.textContent = "Send code";
        return;
      }
      switchToOtpScreen(identityForOtpScreen, data.devCode, channel);
    } catch (err: any) {
      const aborted = err?.name === "AbortError";
      phoneErr.textContent = aborted
        ? "Took too long. Check your connection and try again."
        : "Network error — try again in a moment.";
      phoneErr.setAttribute("data-show", "1");
      phoneCta.disabled = false;
      phoneCta.textContent = "Send code";
    }
  });

  // ---- OTP screen
  const inputs = Array.from(
    m.querySelectorAll<HTMLInputElement>('[data-screen="otp"] .am-otp-row input')
  );
  const otpCta = m.querySelector('[data-screen="otp"] .am-cta') as HTMLButtonElement;
  const otpErr = m.querySelector('[data-screen="otp"] .am-err') as HTMLElement;
  const resendBtn = m.querySelector(".am-resend-btn") as HTMLButtonElement;
  const resendCd = m.querySelector(".am-resend-cd") as HTMLSpanElement;
  const updateOtpCta = () => {
    const code = inputs.map((i) => i.value).join("");
    otpCta.disabled = code.length !== 6;
  };
  for (let i = 0; i < inputs.length; i++) {
    const input = inputs[i];
    input.addEventListener("input", () => {
      input.value = input.value.replace(/\D/g, "").slice(0, 1);
      if (input.value && i < inputs.length - 1) inputs[i + 1].focus();
      updateOtpCta();
    });
    input.addEventListener("keydown", (e) => {
      if (e.key === "Backspace" && !input.value && i > 0) inputs[i - 1].focus();
    });
    input.addEventListener("paste", (e) => {
      const pasted = (e.clipboardData?.getData("text") || "").replace(/\D/g, "").slice(0, 6);
      if (pasted) {
        e.preventDefault();
        for (let j = 0; j < inputs.length; j++) {
          inputs[j].value = pasted[j] || "";
        }
        updateOtpCta();
        otpCta.focus();
      }
    });
  }

  otpCta.addEventListener("click", async () => {
    otpErr.removeAttribute("data-show");
    const code = inputs.map((i) => i.value).join("");
    if (code.length !== 6) return;
    otpCta.disabled = true;
    otpCta.textContent = "Verifying…";
    try {
      // Dispatch by channel — email path sends {email,code}, phone path
      // sends {phone,code}. Server enforces the same precedence rule (email
      // wins if both present), but we send only the relevant field so a
      // client-side bug can't cross the wires.
      const verifyBody = phoneState.channel === "email"
        ? { email: phoneState.email, code }
        : { phone: phoneState.phone, code };
      const res = await authFetch("/api/auth/verify-otp", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(verifyBody),
      });
      if (!res.ok) {
        otpErr.textContent = await reasonFromResponse(res, "Couldn't verify the code. Try again.");
        otpErr.setAttribute("data-show", "1");
        otpCta.disabled = false;
        otpCta.textContent = "Verify & sign in";
        return;
      }
      const data = await res.json().catch(() => ({} as any));
      if (!data || !data.ok) {
        otpErr.textContent = data?.message || "Couldn't verify the code. Try again.";
        otpErr.setAttribute("data-show", "1");
        otpCta.disabled = false;
        otpCta.textContent = "Verify & sign in";
        return;
      }
      // Success — refresh pill, close modal.
      CURRENT_USER = data.user;
      try { localStorage.setItem(NAME_CACHE_KEY, JSON.stringify(data.user)); } catch { /* ignore */ }
      setPillState(data.user);
      closeAuthModal();
      // Pulse the pill so the user sees they're signed in.
      pill?.animate(
        [{ transform: "scale(1)" }, { transform: "scale(1.1)" }, { transform: "scale(1)" }],
        { duration: 320, easing: "ease-out" }
      );
    } catch (err: any) {
      const aborted = err?.name === "AbortError";
      otpErr.textContent = aborted
        ? "Took too long. Try again."
        : "Network error — try again.";
      otpErr.setAttribute("data-show", "1");
      otpCta.disabled = false;
      otpCta.textContent = "Verify & sign in";
    }
  });

  // Resend logic — 60s cooldown
  let resendCdTimer: number | null = null;
  const startResendCooldown = () => {
    let s = 60;
    resendBtn.disabled = true;
    resendCd.textContent = ` (${s}s)`;
    if (resendCdTimer) window.clearInterval(resendCdTimer);
    resendCdTimer = window.setInterval(() => {
      s -= 1;
      resendCd.textContent = ` (${s}s)`;
      if (s <= 0) {
        if (resendCdTimer) window.clearInterval(resendCdTimer);
        resendBtn.disabled = false;
        resendCd.textContent = "";
      }
    }, 1000);
  };

  resendBtn.addEventListener("click", async () => {
    resendBtn.disabled = true;
    try {
      // Same channel-aware body shape as the initial request — resend should
      // hit the SAME identity that we're verifying, otherwise the new OTP
      // record would land under a different hash and verify-otp would say
      // "no-code" forever.
      const resendBody = phoneState.channel === "email"
        ? { email: phoneState.email }
        : { phone: phoneState.phone };
      const res = await authFetch("/api/auth/request-otp", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(resendBody),
      });
      const data = await res.json().catch(() => ({} as any));
      if (res.ok && data?.ok) {
        if (data.devCode) {
          const dc = m.querySelector(".am-devcode") as HTMLElement;
          dc.textContent = `Dev mode: code is ${data.devCode}`;
          dc.setAttribute("data-show", "1");
        }
        startResendCooldown();
      } else {
        resendBtn.disabled = false;
      }
    } catch {
      resendBtn.disabled = false;
    }
  });

  // Stash for later use
  (m as any).__startResendCooldown = startResendCooldown;
}

/**
 * Show the OTP-entry screen with the right destination masked at the top.
 * `channel` controls the masking shape: phone numbers reveal head + tail
 * (e.g. "+61 *** 891"), emails reveal first char + domain (e.g. "k****@gmail.com")
 * which is the user-recognisable shape WhatsApp / Apple use.
 */
function switchToOtpScreen(identity: string, devCode: string | undefined, channel: "phone" | "email") {
  if (!modal) return;
  const m = modal;
  (m.querySelector('[data-screen="phone"]') as HTMLElement).hidden = true;
  (m.querySelector('[data-screen="otp"]') as HTMLElement).hidden = false;
  (m.querySelector(".am-phonemask") as HTMLElement).textContent = channel === "email"
    ? maskEmail(identity)
    : mask(identity);
  const dc = m.querySelector(".am-devcode") as HTMLElement;
  if (devCode) {
    dc.textContent = `Dev mode: code is ${devCode}`;
    dc.setAttribute("data-show", "1");
  } else {
    dc.removeAttribute("data-show");
    dc.textContent = "";
  }
  // Reset inputs
  m.querySelectorAll<HTMLInputElement>('[data-screen="otp"] .am-otp-row input').forEach((i) => (i.value = ""));
  const cta = m.querySelector('[data-screen="otp"] .am-cta') as HTMLButtonElement;
  cta.disabled = true;
  cta.textContent = "Verify & sign in";
  (m as any).__startResendCooldown?.();
  setTimeout(() => {
    (m.querySelector<HTMLInputElement>('[data-screen="otp"] .am-otp-row input'))?.focus();
  }, 60);
}

function mask(phone: string): string {
  if (phone.length < 5) return phone;
  return phone.slice(0, 3) + " " + "*".repeat(Math.max(0, phone.length - 6)) + " " + phone.slice(-3);
}

/**
 * Mask an email for display: k****@gmail.com.
 * Mirrors functions/_lib/email.js::maskEmail. We re-implement client-side
 * (instead of calling the server) so the OTP screen renders instantly.
 */
function maskEmail(email: string): string {
  const at = email.indexOf("@");
  if (at < 1) return email;
  const local = email.slice(0, at);
  const domain = email.slice(at);
  const head = local.slice(0, 1);
  const stars = "*".repeat(Math.max(3, local.length - 1));
  return `${head}${stars}${domain}`;
}

export function openAuthModal(): void {
  const m = ensureModal();
  m.setAttribute("data-open", "1");
  // Reset to phone screen + phone tab. We don't blow away typed values
  // (a user who toggled to Email then closed by accident shouldn't have
  // to re-type) — only the screen and active-tab state.
  (m.querySelector('[data-screen="phone"]') as HTMLElement).hidden = false;
  (m.querySelector('[data-screen="otp"]') as HTMLElement).hidden = true;
  // Default to the phone tab on open. A user who specifically wants email
  // is one tap away.
  const tabs = Array.from(m.querySelectorAll<HTMLButtonElement>(".am-tab"));
  for (const t of tabs) t.setAttribute("aria-selected", t.dataset.channel === "phone" ? "true" : "false");
  (m.querySelector('[data-channel-row="phone"]') as HTMLElement).setAttribute("data-active", "1");
  (m.querySelector('[data-channel-row="email"]') as HTMLElement).setAttribute("data-active", "0");
  phoneState.channel = "phone";
  (m.querySelector('input.am-phone') as HTMLInputElement)?.focus();
}

export function closeAuthModal(): void {
  if (!modal) return;
  modal.removeAttribute("data-open");
}

/* -------------------------------------------------------------------------- */
/*  Public for /account                                                       */
/* -------------------------------------------------------------------------- */

export async function fetchMe(): Promise<MeUser | null> {
  try {
    const res = await authFetch("/api/auth/me", { cache: "no-store" });
    if (!res.ok) return null;
    const data = await res.json();
    if (data?.ok && data.user) {
      CURRENT_USER = data.user;
      try { localStorage.setItem(NAME_CACHE_KEY, JSON.stringify(data.user)); } catch { /* ignore */ }
      return data.user;
    }
  } catch { /* ignore */ }
  return null;
}

export async function logout(): Promise<void> {
  try {
    await authFetch("/api/auth/logout", { method: "POST" });
  } catch { /* ignore */ }
  CURRENT_USER = null;
  try { localStorage.removeItem(NAME_CACHE_KEY); } catch { /* ignore */ }
  setPillState(null);
}

export async function updateDisplayName(name: string): Promise<MeUser | null> {
  try {
    const res = await authFetch("/api/auth/me", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ displayName: name }),
    });
    const data = await res.json().catch(() => ({} as any));
    if (data?.ok && data.user) {
      CURRENT_USER = data.user;
      try { localStorage.setItem(NAME_CACHE_KEY, JSON.stringify(data.user)); } catch { /* ignore */ }
      setPillState(data.user);
      return data.user;
    }
  } catch { /* ignore */ }
  return null;
}

export function getCurrentUser(): MeUser | null {
  return CURRENT_USER;
}
